import React, { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import { 
  ChevronRight, ChevronDown, AlertTriangle, GitBranch, Calendar, 
  DollarSign, MoreHorizontal, Plus, Edit2, Trash2, Flag, Lock,
  ZoomIn, ZoomOut, Maximize2
} from 'lucide-react';
import { UnifiedTooltip } from './UnifiedTooltip';
import { useTenant } from '../context/TenantContext';
import { RoadmapRiskIndicator } from './GovernanceCountColumns';

// Status color mapping - Using CSS variable names for consistency
// These map to --clr-sev-* tokens from the design system
const STATUS_COLORS = {
  'Blocked': 'var(--clr-sev-blocked)',         // #D32F2F
  'Behind': 'var(--clr-sev-behind)',           // #C62828
  'Due Date at Risk': 'var(--clr-sev-atrisk)', // #F57C00
  'At Risk': 'var(--clr-sev-atrisk)',          // #F57C00
  'In Progress': 'var(--clr-sev-inprogress)',  // #5E81F4
  'In Review': 'var(--clr-sev-inprogress)',    // #3949AB
  'Not Started': 'var(--clr-sev-notstarted)',  // #9E9E9E
  'On Track': 'var(--clr-sev-healthy)',        // #388E3C
  'Completed': 'var(--clr-sev-healthy)',       // #388E3C
  'Not Required': '#757575',
  'Archived': '#616161',
  'Not Set': 'var(--clr-sev-notstarted)'       // Grey for unset status
};

// Fallback hex colors for when CSS vars aren't available (e.g., in calculations)
const STATUS_HEX_COLORS = {
  'Blocked': '#D32F2F',
  'Behind': '#C62828',
  'Due Date at Risk': '#F57C00',
  'At Risk': '#F57C00',
  'In Progress': '#5E81F4',
  'In Review': '#3949AB',
  'Not Started': '#9E9E9E',
  'On Track': '#388E3C',
  'Completed': '#388E3C',
  'Not Required': '#757575',
  'Archived': '#616161',
  'Not Set': '#9E9E9E'
};

// Type colors for badges
const TYPE_COLORS = {
  'Portfolio': '#9B59B6',
  'Program': '#3498DB',
  'Project': '#16A085',
  'Epic': '#6366F1',
  'Feature': '#22C55E',
  'Story': '#06B6D4',
  'Task': '#6B7280',
  'Milestone': '#F59E0B'
};

// Configuration
const ROW_HEIGHT = 44;
const HEADER_HEIGHT = 52;
const LEVEL_INDENT = 24;
const MIN_BAR_WIDTH = 8;
const LEFT_PANEL_WIDTH = 320;

// Zoom levels configuration - determines pixels per day
const ZOOM_LEVELS = {
  'fit': { label: 'Fit to Screen', pixelsPerDay: null },  // Dynamic - fills container
  'year': { label: 'Year View', pixelsPerDay: 0.8 },
  'quarter': { label: 'Quarter View', pixelsPerDay: 2 },
  'month': { label: 'Month View', pixelsPerDay: 6 },
  'week': { label: 'Week View', pixelsPerDay: 20 }
};

// Scale configurations for header labels
const SCALE_CONFIG = {
  year: { format: '{year}', getDivisions: (start, end) => getYearDivisions(start, end) },
  quarter: { format: 'Q{q} {year}', getDivisions: (start, end) => getQuarterDivisions(start, end) },
  month: { format: '{month} {year}', getDivisions: (start, end) => getMonthDivisions(start, end) }
};

// Date utilities
const formatCurrency = (value, currency = 'ZAR') => {
  const symbols = { ZAR: 'R', USD: '$', EUR: '€', GBP: '£' };
  const symbol = symbols[currency] || currency;
  if (Math.abs(value) >= 1000000) return `${symbol}${(value / 1000000).toFixed(1)}M`;
  if (Math.abs(value) >= 1000) return `${symbol}${(value / 1000).toFixed(1)}K`;
  return `${symbol}${value?.toFixed(0) || 0}`;
};

const parseDate = (dateStr) => {
  if (!dateStr) return null;
  const d = new Date(dateStr);
  return isNaN(d.getTime()) ? null : d;
};

const daysBetween = (d1, d2) => {
  if (!d1 || !d2) return 0;
  return Math.ceil((d2 - d1) / (1000 * 60 * 60 * 24));
};

// Get month divisions for header labels
const getMonthDivisions = (startDate, endDate) => {
  const divisions = [];
  let current = new Date(startDate);
  current.setDate(1);
  
  while (current <= endDate) {
    const month = current.toLocaleString('en-US', { month: 'short' });
    const year = current.getFullYear();
    const daysInMonth = new Date(year, current.getMonth() + 1, 0).getDate();
    
    divisions.push({
      date: new Date(current),
      label: `${month} ${year}`,
      days: daysInMonth
    });
    
    current.setMonth(current.getMonth() + 1);
  }
  return divisions;
};

// Get quarter divisions for header labels
const getQuarterDivisions = (startDate, endDate) => {
  const divisions = [];
  let current = new Date(startDate);
  current.setMonth(Math.floor(current.getMonth() / 3) * 3); // Start of quarter
  current.setDate(1);
  
  while (current <= endDate) {
    const q = Math.ceil((current.getMonth() + 1) / 3);
    const year = current.getFullYear();
    const quarterStart = new Date(year, (q - 1) * 3, 1);
    const quarterEnd = new Date(year, q * 3, 0);
    const days = daysBetween(quarterStart, quarterEnd) + 1;
    
    divisions.push({
      date: new Date(current),
      label: `Q${q} ${year}`,
      days
    });
    
    current.setMonth(current.getMonth() + 3);
  }
  return divisions;
};

// Get year divisions for header labels
const getYearDivisions = (startDate, endDate) => {
  const divisions = [];
  let current = new Date(startDate.getFullYear(), 0, 1);
  
  while (current <= endDate) {
    const year = current.getFullYear();
    const isLeap = (year % 4 === 0 && year % 100 !== 0) || (year % 400 === 0);
    
    divisions.push({
      date: new Date(current),
      label: `${year}`,
      days: isLeap ? 366 : 365
    });
    
    current.setFullYear(current.getFullYear() + 1);
  }
  return divisions;
};

// Generate timeline divisions based on scale
const generateTimeline = (startDate, endDate, scale) => {
  if (scale === 'year') return getYearDivisions(startDate, endDate);
  if (scale === 'quarter') return getQuarterDivisions(startDate, endDate);
  return getMonthDivisions(startDate, endDate);
};

// Simplified TimelineBar Component - Only renders the bar portion
const TimelineBar = ({ item, timelineStart, totalDays, timelineWidth, onItemClick, onOpenInspector, currency, tenantId }) => {
  const [showTooltip, setShowTooltip] = useState(false);
  const [tooltipPos, setTooltipPos] = useState({ x: 0, y: 0 });
  const barRef = useRef(null);
  
  const startDate = parseDate(item.planned_start || item.start_date);
  const endDate = parseDate(item.planned_end || item.due_date);
  const revisedDate = parseDate(item.revised_end || item.revised_date);
  
  // Calculate bar position using percentages
  const getBarPosition = () => {
    if (!startDate || !endDate) return null;
    
    const startOffset = Math.max(0, daysBetween(timelineStart, startDate));
    const duration = Math.max(1, daysBetween(startDate, endDate));
    
    const leftPct = (startOffset / totalDays) * 100;
    const widthPct = Math.max(1, (duration / totalDays) * 100);
    
    return { leftPct, widthPct };
  };
  
  const barPos = getBarPosition();
  const statusColor = STATUS_COLORS[item.status] || STATUS_COLORS['Not Started'];
  const progress = item.percent_complete || 0;
  
  // Handle mouse move for tooltip position
  const handleMouseMove = (e) => {
    setTooltipPos({ x: e.clientX, y: e.clientY - 10 });
  };
  
  // Create tooltip data
  const tooltipData = {
    name: item.name,
    type: item.type || 'Item',
    status: item.status,
    percent_complete: progress,
    dates: {
      start: item.planned_start || item.start_date,
      due: item.planned_end || item.due_date,
      revised: item.revised_end || item.revised_date
    },
    cost: {
      est: item.est_cost,
      act: item.act_cost,
      var: item.cost_variance,
      currency
    },
    counts: {
      issues: item.issues_count || 0,
      dependencies: item.deps_count || 0
    }
  };
  
  return (
    <div 
      className="relative"
      style={{ height: ROW_HEIGHT, width: '100%' }}
    >
      {barPos && (
        <div
          ref={barRef}
          className="absolute top-2 cursor-pointer transition-all duration-150 hover:shadow-md"
          style={{
            left: `${barPos.leftPct}%`,
            width: `${barPos.widthPct}%`,
            minWidth: MIN_BAR_WIDTH,
            height: ROW_HEIGHT - 16,
            backgroundColor: '#F3F4F6',
            borderRadius: 4,
            border: '1px solid #E5E7EB',
            zIndex: 3
          }}
          onClick={() => onItemClick && onItemClick(item)}
          onMouseEnter={() => setShowTooltip(true)}
          onMouseLeave={() => setShowTooltip(false)}
          onMouseMove={handleMouseMove}
        >
          {/* Progress Fill */}
          <div
            className="absolute top-0 left-0 bottom-0 rounded-l transition-all"
            style={{
              width: `${Math.min(100, progress)}%`,
              backgroundColor: statusColor,
              opacity: 0.8,
              borderRadius: progress >= 100 ? 4 : '4px 0 0 4px'
            }}
          />
          
          {/* Progress Label */}
          {barPos.widthPct > 5 && (
            <span 
              className="absolute inset-0 flex items-center justify-center text-xs font-semibold"
              style={{ 
                color: progress > 50 ? 'white' : statusColor,
                textShadow: progress > 50 ? '0 1px 2px rgba(0,0,0,0.3)' : 'none'
              }}
            >
              {progress.toFixed(0)}%
            </span>
          )}
          
          {/* Revised Date Overlay (slippage indicator) */}
          {revisedDate && revisedDate > endDate && (
            <div
              className="absolute top-0 bottom-0"
              style={{
                left: '100%',
                width: `${(daysBetween(endDate, revisedDate) / totalDays) * 100}%`,
                background: `repeating-linear-gradient(45deg, transparent, transparent 2px, ${statusColor}30 2px, ${statusColor}30 4px)`,
                borderRadius: '0 4px 4px 0',
                border: `1px dashed ${statusColor}60`,
                borderLeft: 'none',
                zIndex: 4
              }}
            />
          )}
          
          {/* High-severity Risk Warning Indicator */}
          <RoadmapRiskIndicator
            workId={item.id}
            tenantId={tenantId}
            onOpenInspector={onOpenInspector}
            position={{ right: 4, top: '50%' }}
          />
          
          {/* Tooltip */}
          {showTooltip && (
            <UnifiedTooltip
              data={tooltipData}
              position={tooltipPos}
              variant="detail"
            />
          )}
        </div>
      )}
      
      {/* No dates indicator */}
      {!barPos && (
        <div className="absolute inset-x-4 top-3 bottom-3 flex items-center justify-center">
          <span className="text-xs text-gray-400 italic">No dates set</span>
        </div>
      )}
    </div>
  );
};

// Transform item to tooltip data format
const itemToTooltipData = (item, currency = 'ZAR') => ({
  name: item.name,
  type: item.type || 'Item',
  status: item.status,
  percent_complete: item.percent_complete || 0,
  dates: {
    start: item.planned_start || item.start_date,
    due: item.planned_end || item.due_date,
    revised: item.revised_end || item.revised_date
  },
  cost: {
    est: item.hours_estimated ? item.hours_estimated * 100 : item.est_cost,
    act: item.hours_actual ? item.hours_actual * 100 : item.act_cost,
    var: item.cost_variance,
    currency
  },
  counts: {
    issues: item.issues_count || 0,
    dependencies: item.deps_count || 0
  },
  owner: item.owner,
  sprints: item.sprints || [],
  release: item.release_name
});

// Row Component
const RoadmapRow = ({ 
  item, 
  level, 
  isExpanded, 
  onToggle, 
  timelineStart, 
  timelineEnd,
  totalDays,
  timelineWidth,
  onItemClick,
  onContextMenu,
  onDateChange,
  canEdit,
  currency
}) => {
  const [showTooltip, setShowTooltip] = useState(false);
  const [tooltipPos, setTooltipPos] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(null); // 'start', 'end', or 'move'
  const [dragOffset, setDragOffset] = useState(0);
  const rowRef = useRef(null);
  const barRef = useRef(null);
  const timelineRef = useRef(null);
  
  const hasChildren = item.children && item.children.length > 0;
  const indent = level * LEVEL_INDENT;
  
  // Calculate bar position
  const startDate = parseDate(item.planned_start || item.start_date);
  const endDate = parseDate(item.planned_end || item.due_date);
  const revisedDate = parseDate(item.revised_end || item.revised_date);
  
  // Use percentage-based positioning - bars fill 100% of available timeline width
  const getBarPosition = () => {
    if (!startDate || !endDate) return null;
    
    const startOffset = Math.max(0, daysBetween(timelineStart, startDate));
    const duration = Math.max(1, daysBetween(startDate, endDate));
    
    // Convert to percentages of total timeline
    const leftPct = (startOffset / totalDays) * 100;
    const widthPct = Math.max(1, (duration / totalDays) * 100); // Minimum 1%
    
    let revisedWidthPct = 0;
    if (revisedDate && revisedDate > endDate) {
      const revisedDuration = daysBetween(endDate, revisedDate);
      revisedWidthPct = (revisedDuration / totalDays) * 100;
    }
    
    return { leftPct, widthPct, revisedWidthPct };
  };
  
  const barPos = getBarPosition();
  const statusColor = STATUS_COLORS[item.status] || '#95A5A6';
  const typeColor = TYPE_COLORS[item.type] || '#6B7280';
  
  // Convert pixel offset to days (for drag)
  const pixelsToDays = (pixels) => {
    if (!timelineRef.current) return 0;
    const containerWidth = timelineRef.current.offsetWidth;
    return Math.round((pixels / containerWidth) * totalDays);
  };
  
  // Handle drag start
  const handleDragStart = (e, type) => {
    if (!canEdit) return;
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(type);
    setShowTooltip(false);
    
    const startX = e.clientX;
    
    const handleMouseMove = (moveEvent) => {
      const delta = moveEvent.clientX - startX;
      setDragOffset(delta);
    };
    
    const handleMouseUp = async () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      
      const daysDelta = pixelsToDays(dragOffset);
      if (daysDelta !== 0 && onDateChange) {
        const newStartDate = new Date(startDate);
        const newEndDate = new Date(endDate);
        
        if (type === 'start' || type === 'move') {
          newStartDate.setDate(newStartDate.getDate() + daysDelta);
        }
        if (type === 'end' || type === 'move') {
          newEndDate.setDate(newEndDate.getDate() + daysDelta);
        }
        
        // Validate: end must be after start
        if (newEndDate > newStartDate) {
          await onDateChange(item, {
            planned_start: type !== 'end' ? newStartDate.toISOString() : undefined,
            planned_end: type !== 'start' ? newEndDate.toISOString() : undefined
          });
        }
      }
      
      setIsDragging(null);
      setDragOffset(0);
    };
    
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  };
  
  // Calculate adjusted bar position during drag (convert pixel offset to percentage)
  const getAdjustedBarPos = () => {
    if (!barPos || !isDragging) return barPos;
    
    const adjustedPos = { ...barPos };
    const containerWidth = timelineRef.current?.offsetWidth || 1000;
    const offsetPct = (dragOffset / containerWidth) * 100;
    
    if (isDragging === 'start') {
      adjustedPos.leftPct += offsetPct;
      adjustedPos.widthPct -= offsetPct;
    } else if (isDragging === 'end') {
      adjustedPos.widthPct += offsetPct;
    } else if (isDragging === 'move') {
      adjustedPos.leftPct += offsetPct;
    }
    
    return adjustedPos;
  };
  
  const displayBarPos = getAdjustedBarPos();
  
  const handleMouseEnter = (e) => {
    if (isDragging) return;
    if (barRef.current) {
      const rect = barRef.current.getBoundingClientRect();
      setTooltipPos({ x: rect.left, y: rect.bottom + 8 });
    }
    setShowTooltip(true);
  };
  
  const handleContextMenu = (e) => {
    e.preventDefault();
    if (canEdit && onContextMenu) {
      onContextMenu(e, item);
    }
  };
  
  return (
    <div 
      ref={rowRef}
      className="flex border-b border-gray-100 hover:bg-[var(--clr-surface-hover)]/50 group"
      style={{ height: ROW_HEIGHT }}
      data-testid={`roadmap-row-${item.id}`}
    >
      {/* Left Panel - Item Info - sticky */}
      <div 
        className="flex-shrink-0 flex items-center border-r border-gray-200 bg-white sticky left-0"
        style={{ paddingLeft: indent + 8, zIndex: 20, width: LEFT_PANEL_WIDTH }}
      >
        {/* Expand/Collapse */}
        {hasChildren ? (
          <button 
            onClick={() => onToggle(item.id)}
            className="p-1 hover:bg-[var(--clr-surface-hover)] rounded mr-1"
            aria-label={isExpanded ? 'Collapse' : 'Expand'}
          >
            {isExpanded ? (
              <ChevronDown className="w-4 h-4 text-[var(--clr-text-muted)]" />
            ) : (
              <ChevronRight className="w-4 h-4 text-[var(--clr-text-muted)]" />
            )}
          </button>
        ) : (
          <div className="w-6" />
        )}
        
        {/* Type Badge */}
        <span 
          className="w-1.5 h-6 rounded-sm mr-2 flex-shrink-0"
          style={{ backgroundColor: typeColor }}
          title={item.type}
        />
        
        {/* Name */}
        <span 
          className="truncate text-sm font-medium text-[var(--clr-text-primary)] cursor-pointer hover:text-[var(--clr-brand-primary)]"
          data-entity-type={item.type?.toLowerCase() || 'item'}
          data-entity-id={item.id}
          onClick={(e) => {
            e.stopPropagation();
            if (onItemClick) {
              try {
                onItemClick(item);
              } catch (err) {
                console.error('Roadmap name click error:', err);
              }
            }
          }}
          title={item.name}
        >
          {item.name}
        </span>
        
        {/* Counts */}
        <div className="ml-auto flex items-center gap-1 pr-2 opacity-0 group-hover:opacity-100">
          {item.issues_count > 0 && (
            <span className="flex items-center text-xs text-[var(--clr-sev-atrisk)]">
              <AlertTriangle className="w-3 h-3 mr-0.5" />
              {item.issues_count}
            </span>
          )}
          {item.deps_count > 0 && (
            <span className="flex items-center text-xs text-[var(--clr-brand-primary)]">
              <GitBranch className="w-3 h-3 mr-0.5" />
              {item.deps_count}
            </span>
          )}
        </div>
      </div>
      
      {/* Right Panel - Timeline - Full width with percentage-based bars */}
      <div 
        ref={timelineRef}
        className="flex-1 relative"
        style={{ width: timelineWidth || '100%' }}
        onContextMenu={handleContextMenu}
      >
        {displayBarPos && (
          <>
            {/* Main Bar - z-[3] - FULL SEMANTIC COLOR, NO REDUCED OPACITY */}
            <div
              ref={barRef}
              className={`absolute top-2 h-7 rounded cursor-pointer transition-all ${isDragging ? 'ring-2 ring-blue-500' : 'hover:brightness-110 hover:scale-y-105'}`}
              style={{
                left: `${displayBarPos.leftPct}%`,
                width: `${Math.max(2, displayBarPos.widthPct)}%`,
                backgroundColor: statusColor,
                zIndex: 3
              }}
              data-entity-type={item.type?.toLowerCase() || 'item'}
              data-entity-id={item.id}
              data-testid={`roadmap-bar-${item.id}`}
              onMouseEnter={handleMouseEnter}
              onMouseLeave={() => !isDragging && setShowTooltip(false)}
              onClick={(e) => {
                e.stopPropagation();
                if (!isDragging && onItemClick) {
                  try {
                    onItemClick(item);
                  } catch (err) {
                    console.error('Roadmap click handler error:', err);
                  }
                }
              }}
              onMouseDown={(e) => canEdit && handleDragStart(e, 'move')}
            >
              {/* Progress fill */}
              <div 
                className="absolute inset-y-0 left-0 bg-white/20 rounded-l"
                style={{ width: `${item.percent_complete || 0}%` }}
              />
              
              {/* Bar label - Show only if bar is wide enough */}
              {displayBarPos.widthPct > 5 && (
                <span className="absolute inset-0 flex items-center justify-center text-xs text-white font-medium pointer-events-none">
                  {item.percent_complete || 0}%
                </span>
              )}
              
              {/* Left drag handle (adjust start date) */}
              {canEdit && (
                <div
                  className="absolute left-0 top-0 bottom-0 w-2 cursor-ew-resize hover:bg-white/30 rounded-l group/handle"
                  onMouseDown={(e) => handleDragStart(e, 'start')}
                  data-testid={`drag-start-${item.id}`}
                >
                  <div className="absolute left-0.5 top-1/2 -translate-y-1/2 w-0.5 h-3 bg-white/50 rounded opacity-0 group-hover/handle:opacity-100" />
                </div>
              )}
              
              {/* Right drag handle (adjust end date) */}
              {canEdit && (
                <div
                  className="absolute right-0 top-0 bottom-0 w-2 cursor-ew-resize hover:bg-white/30 rounded-r group/handle"
                  onMouseDown={(e) => handleDragStart(e, 'end')}
                  data-testid={`drag-end-${item.id}`}
                >
                  <div className="absolute right-0.5 top-1/2 -translate-y-1/2 w-0.5 h-3 bg-white/50 rounded opacity-0 group-hover/handle:opacity-100" />
                </div>
              )}
            </div>
            
            {/* Revised overlay (slippage) - z-[4] */}
            {displayBarPos.revisedWidthPct > 0 && (
              <div
                className="absolute top-2 h-7 rounded-r"
                style={{
                  left: `${displayBarPos.leftPct + displayBarPos.widthPct}%`,
                  width: `${displayBarPos.revisedWidthPct}%`,
                  backgroundColor: '#C62828',
                  backgroundImage: 'repeating-linear-gradient(45deg, transparent, transparent 3px, rgba(255,255,255,0.2) 3px, rgba(255,255,255,0.2) 6px)',
                  zIndex: 4
                }}
              />
            )}
            
            {/* Milestone dots - z-[5] - ABOVE bars and release bands */}
            {item.type === 'Milestone' && (
              <div
                className="absolute top-1/2 -translate-y-1/2 w-4 h-4 rounded-full border-2 border-white shadow-lg"
                style={{
                  left: `calc(${displayBarPos.leftPct}% - 8px)`,
                  backgroundColor: statusColor,
                  zIndex: 5
                }}
              />
            )}
          </>
        )}
        
        {/* Drag indicator - z-[50] */}
        {isDragging && (
          <div 
            className="fixed top-4 left-1/2 -translate-x-1/2 bg-gray-900 text-white px-3 py-1.5 rounded-lg text-sm shadow-lg"
            style={{ zIndex: 50 }}
          >
            {isDragging === 'start' && `Start: ${pixelsToDays(dragOffset)} days`}
            {isDragging === 'end' && `End: ${pixelsToDays(dragOffset)} days`}
            {isDragging === 'move' && `Move: ${pixelsToDays(dragOffset)} days`}
          </div>
        )}
        
        {/* Tooltip - z-[9999] - ALWAYS on top */}
        {showTooltip && !isDragging && (
          <div 
            className="fixed"
            style={{ 
              top: tooltipPos.y, 
              left: tooltipPos.x,
              zIndex: 9999 
            }}
          >
            <UnifiedTooltip
              data={itemToTooltipData(item, currency)}
              visible={true}
              onClick={() => onItemClick && onItemClick(item)}
              currency={currency}
            />
          </div>
        )}
      </div>
    </div>
  );
};

// Main Hierarchical Roadmap Component
const HierarchicalRoadmap = ({
  data = [],
  scale = 'month',
  showReleaseBands = false,
  releases = [],
  onItemClick,
  onCreateItem,
  onEditItem,
  onDeleteItem,
  onDateChange,
  onOpenInspector,  // Callback to open Inspector at specific tab
  tenantId,         // For governance API calls
  currency = 'ZAR',
  maxDepth = 7,
  initialExpanded = new Set(),
  timelineRange = null,
  viewType = null,
  onScaleChange = null  // Optional callback when scale changes
}) => {
  const { canEdit } = useTenant();
  const [expandedItems, setExpandedItems] = useState(initialExpanded);
  const [contextMenu, setContextMenu] = useState(null);
  const [zoomLevel, setZoomLevel] = useState('fit'); // 'fit', 'year', 'quarter', 'month', 'week'
  const [customZoom, setCustomZoom] = useState(1.0); // Multiplier for custom zoom
  const containerRef = useRef(null);
  const timelinePanelRef = useRef(null);
  const [containerWidth, setContainerWidth] = useState(800);
  
  // Measure container width on mount and resize
  useEffect(() => {
    const updateWidth = () => {
      if (containerRef.current) {
        const width = containerRef.current.offsetWidth - LEFT_PANEL_WIDTH;
        setContainerWidth(Math.max(400, width));
      }
    };
    
    updateWidth();
    const resizeObserver = new ResizeObserver(updateWidth);
    if (containerRef.current) {
      resizeObserver.observe(containerRef.current);
    }
    
    return () => resizeObserver.disconnect();
  }, []);
  
  // Calculate timeline range from data OR use provided range
  const { timelineStart, timelineEnd } = useMemo(() => {
    if (timelineRange?.start && timelineRange?.end) {
      return {
        timelineStart: parseDate(timelineRange.start) || new Date(),
        timelineEnd: parseDate(timelineRange.end) || new Date()
      };
    }
    
    let minDate = null;
    let maxDate = null;
    
    const findDates = (items) => {
      if (!items || !Array.isArray(items)) return;
      items.forEach(item => {
        const start = parseDate(item.planned_start || item.start_date);
        const end = parseDate(item.revised_end || item.planned_end || item.due_date);
        
        if (start) {
          if (minDate === null || start < minDate) minDate = start;
        }
        if (end) {
          if (maxDate === null || end > maxDate) maxDate = end;
        }
        
        if (item.children) findDates(item.children);
      });
    };
    
    if (data && Array.isArray(data)) {
      findDates(data);
    }
    
    if (minDate === null) minDate = new Date();
    if (maxDate === null) {
      maxDate = new Date();
      maxDate.setMonth(maxDate.getMonth() + 12);
    }
    
    // No left buffer - timeline starts at the earliest item's date
    const bufferEnd = new Date(maxDate);
    bufferEnd.setDate(bufferEnd.getDate() + 30);
    
    return { timelineStart: minDate, timelineEnd: bufferEnd };
  }, [data, timelineRange]);
  
  // Calculate total days in timeline
  const totalDays = useMemo(() => {
    return Math.max(1, daysBetween(timelineStart, timelineEnd));
  }, [timelineStart, timelineEnd]);
  
  // Calculate timeline width based on zoom level
  const timelineWidth = useMemo(() => {
    if (zoomLevel === 'fit') {
      // Fit to container - always fills 100%
      return containerWidth * customZoom;
    }
    // Fixed pixels per day based on zoom level
    const ppd = ZOOM_LEVELS[zoomLevel]?.pixelsPerDay || 2;
    return totalDays * ppd * customZoom;
  }, [zoomLevel, containerWidth, totalDays, customZoom]);
  
  // Generate timeline header divisions
  const timeline = useMemo(() => 
    generateTimeline(timelineStart, timelineEnd, scale),
    [timelineStart, timelineEnd, scale]
  );
  
  // Calculate width per division for header rendering
  const divisionWidths = useMemo(() => {
    if (timeline.length === 0) return [];
    
    // Calculate total days across all divisions
    const totalTimelineDays = timeline.reduce((sum, div) => sum + (div.days || 30), 0);
    
    // Calculate width as percentage of timeline
    return timeline.map(div => {
      const dayRatio = (div.days || 30) / totalTimelineDays;
      return timelineWidth * dayRatio;
    });
  }, [timeline, timelineWidth]);
  
  // Zoom controls
  const handleZoomIn = () => {
    if (zoomLevel === 'fit') {
      setCustomZoom(prev => Math.min(prev * 1.25, 4));
    } else {
      const levels = ['year', 'quarter', 'month', 'week'];
      const currentIdx = levels.indexOf(zoomLevel);
      if (currentIdx < levels.length - 1) {
        setZoomLevel(levels[currentIdx + 1]);
      }
    }
  };
  
  const handleZoomOut = () => {
    if (zoomLevel === 'fit') {
      setCustomZoom(prev => Math.max(prev * 0.8, 0.5));
    } else {
      const levels = ['year', 'quarter', 'month', 'week'];
      const currentIdx = levels.indexOf(zoomLevel);
      if (currentIdx > 0) {
        setZoomLevel(levels[currentIdx - 1]);
      } else {
        setZoomLevel('fit');
      }
    }
  };
  
  const handleFitToScreen = () => {
    setZoomLevel('fit');
    setCustomZoom(1.0);
  };
  
  // Handle mouse wheel zoom (CTRL + wheel)
  useEffect(() => {
    const handleWheel = (e) => {
      if (e.ctrlKey && containerRef.current?.contains(e.target)) {
        e.preventDefault();
        if (e.deltaY < 0) {
          setCustomZoom(prev => Math.min(prev * 1.1, 4));
        } else {
          setCustomZoom(prev => Math.max(prev * 0.9, 0.5));
        }
      }
    };
    
    document.addEventListener('wheel', handleWheel, { passive: false });
    return () => document.removeEventListener('wheel', handleWheel);
  }, []);
  
  // Toggle expand/collapse
  const handleToggle = useCallback((itemId) => {
    setExpandedItems(prev => {
      const next = new Set(prev);
      if (next.has(itemId)) {
        next.delete(itemId);
      } else {
        next.add(itemId);
      }
      return next;
    });
  }, []);
  
  // Flatten tree for rendering
  const flattenedItems = useMemo(() => {
    const result = [];
    
    const flatten = (items, level = 0) => {
      if (level >= maxDepth || !items || !Array.isArray(items)) return;
      
      items.forEach(item => {
        result.push({ item, level, isExpanded: expandedItems.has(item.id) });
        
        if (item.children && item.children.length > 0 && expandedItems.has(item.id)) {
          flatten(item.children, level + 1);
        }
      });
    };
    
    if (data && Array.isArray(data)) {
      flatten(data);
    }
    return result;
  }, [data, expandedItems, maxDepth]);
  
  // Context menu handlers
  const handleContextMenu = (e, item) => {
    e.preventDefault();
    setContextMenu({
      x: e.clientX,
      y: e.clientY,
      item
    });
  };
  
  const closeContextMenu = () => setContextMenu(null);
  
  // Click outside to close context menu
  useEffect(() => {
    const handleClickOutside = () => closeContextMenu();
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);
  
  return (
    <div className="bg-[var(--clr-surface-card)] rounded-lg border shadow-[var(--shadow-card)] overflow-hidden relative" ref={containerRef}>
      {/* Zoom Controls Bar */}
      <div className="flex items-center justify-between px-3 py-1.5 bg-[var(--clr-surface-subtle)] border-b border-gray-200">
        <div className="flex items-center gap-2">
          <span className="text-xs font-medium text-[var(--clr-text-secondary)]">
            {viewType === 'executive' ? 'Strategic Items' : viewType === 'pmo' ? 'Portfolio Items' : 'Project Items'}
          </span>
          <span className="text-xs text-[var(--clr-text-muted)]">({flattenedItems.length})</span>
        </div>
        
        {/* Zoom Controls */}
        <div className="flex items-center gap-1">
          <button
            onClick={handleZoomOut}
            className="p-1 rounded hover:bg-gray-200 text-gray-600"
            title="Zoom Out"
          >
            <ZoomOut className="w-4 h-4" />
          </button>
          
          <button
            onClick={handleFitToScreen}
            className={`px-2 py-0.5 rounded text-xs font-medium ${zoomLevel === 'fit' && customZoom === 1 ? 'bg-blue-100 text-blue-700' : 'hover:bg-gray-200 text-gray-600'}`}
            title="Fit to Screen"
          >
            <Maximize2 className="w-3.5 h-3.5 inline mr-1" />
            Fit
          </button>
          
          <select
            value={zoomLevel}
            onChange={(e) => {
              setZoomLevel(e.target.value);
              setCustomZoom(1.0);
            }}
            className="text-xs border rounded px-1.5 py-0.5 bg-white"
          >
            <option value="fit">Fit to Screen</option>
            <option value="year">Year View</option>
            <option value="quarter">Quarter View</option>
            <option value="month">Month View</option>
            <option value="week">Week View</option>
          </select>
          
          <button
            onClick={handleZoomIn}
            className="p-1 rounded hover:bg-gray-200 text-gray-600"
            title="Zoom In"
          >
            <ZoomIn className="w-4 h-4" />
          </button>
          
          {customZoom !== 1.0 && (
            <span className="text-xs text-gray-500 ml-1">{Math.round(customZoom * 100)}%</span>
          )}
        </div>
      </div>
      
      {/* Main Content - Dual Pane Layout */}
      <div className="flex" style={{ height: 'calc(100vh - 350px)', minHeight: 300 }}>
        {/* LEFT PANE - Fixed Hierarchy Column */}
        <div 
          className="flex-shrink-0 border-r border-gray-200 bg-white"
          style={{ width: LEFT_PANEL_WIDTH }}
        >
          {/* Left Header */}
          <div 
            className="flex items-center px-4 border-b border-gray-200 bg-[var(--clr-surface-subtle)]"
            style={{ height: HEADER_HEIGHT }}
          >
            <span className="text-xs font-medium text-gray-600">Hierarchy</span>
          </div>
          
          {/* Left Body - Synced vertical scroll */}
          <div 
            className="overflow-y-auto overflow-x-hidden"
            style={{ height: `calc(100% - ${HEADER_HEIGHT}px)` }}
            onScroll={(e) => {
              // Sync scroll with right pane
              if (timelinePanelRef.current) {
                const rightBody = timelinePanelRef.current.querySelector('[data-timeline-body]');
                if (rightBody) rightBody.scrollTop = e.target.scrollTop;
              }
            }}
            data-hierarchy-body
          >
            {flattenedItems.length > 0 ? (
              flattenedItems.map(({ item, level, isExpanded }) => (
                <div 
                  key={item.id}
                  className="flex items-center border-b border-gray-100 hover:bg-[var(--clr-surface-hover)]/50"
                  style={{ height: ROW_HEIGHT, paddingLeft: level * LEVEL_INDENT + 8 }}
                  data-testid={`roadmap-row-${item.id}`}
                >
                  {/* Expand/Collapse */}
                  {item.children && item.children.length > 0 ? (
                    <button 
                      onClick={() => handleToggle(item.id)}
                      className="w-5 h-5 flex items-center justify-center hover:bg-gray-200 rounded mr-1"
                    >
                      {isExpanded ? (
                        <ChevronDown className="w-3.5 h-3.5 text-gray-500" />
                      ) : (
                        <ChevronRight className="w-3.5 h-3.5 text-gray-500" />
                      )}
                    </button>
                  ) : (
                    <div className="w-5 mr-1" />
                  )}
                  
                  {/* Type Badge */}
                  <span 
                    className="px-1.5 py-0.5 text-[10px] font-medium rounded mr-2 text-white"
                    style={{ backgroundColor: TYPE_COLORS[item.type] || '#6B7280' }}
                  >
                    {item.type?.charAt(0)}
                  </span>
                  
                  {/* Name */}
                  <span 
                    className="text-sm font-medium text-[var(--clr-text-primary)] truncate cursor-pointer hover:text-[var(--clr-brand-primary)]"
                    style={{ maxWidth: LEFT_PANEL_WIDTH - level * LEVEL_INDENT - 80 }}
                    onClick={() => onItemClick && onItemClick(item)}
                    title={item.name}
                  >
                    {item.name}
                  </span>
                  
                  {/* Status Indicators */}
                  <div className="ml-auto flex items-center gap-1 pr-2">
                    {item.blocked_count > 0 && (
                      <span className="flex items-center text-[10px] text-red-500">
                        <AlertTriangle className="w-3 h-3 mr-0.5" />
                        {item.blocked_count}
                      </span>
                    )}
                    {item.deps_count > 0 && (
                      <span className="flex items-center text-[10px] text-blue-500">
                        <GitBranch className="w-3 h-3 mr-0.5" />
                        {item.deps_count}
                      </span>
                    )}
                  </div>
                </div>
              ))
            ) : (
              <div className="flex items-center justify-center py-12 text-[var(--clr-text-muted)]">
                <Calendar className="w-6 h-6 mr-2 opacity-50" />
                <span className="text-sm">No items</span>
              </div>
            )}
          </div>
        </div>
        
        {/* RIGHT PANE - Scrollable Timeline */}
        <div 
          className="flex-1 overflow-hidden"
          ref={timelinePanelRef}
        >
          {/* Right Header - Horizontal scroll */}
          <div 
            className="overflow-x-auto border-b border-gray-200 bg-[var(--clr-surface-subtle)]"
            style={{ height: HEADER_HEIGHT }}
            onScroll={(e) => {
              // Sync horizontal scroll with body
              const rightBody = e.target.parentElement.querySelector('[data-timeline-body]');
              if (rightBody) rightBody.scrollLeft = e.target.scrollLeft;
            }}
            data-timeline-header
          >
            <div className="flex" style={{ width: timelineWidth, minWidth: '100%' }}>
              {timeline.map((col, idx) => (
                <div 
                  key={idx}
                  className="flex items-center justify-center border-r border-gray-200 text-xs font-medium text-[var(--clr-text-secondary)]"
                  style={{ 
                    width: divisionWidths[idx] || 100,
                    minWidth: 60,
                    height: HEADER_HEIGHT
                  }}
                >
                  {col.label}
                </div>
              ))}
            </div>
          </div>
          
          {/* Right Body - Both scrolls */}
          <div 
            className="overflow-auto relative"
            style={{ height: `calc(100% - ${HEADER_HEIGHT}px)` }}
            onScroll={(e) => {
              // Sync vertical scroll with left pane
              const leftBody = containerRef.current?.querySelector('[data-hierarchy-body]');
              if (leftBody && leftBody.scrollTop !== e.target.scrollTop) {
                leftBody.scrollTop = e.target.scrollTop;
              }
              // Sync horizontal scroll with header
              const header = e.target.parentElement.querySelector('[data-timeline-header]');
              if (header && header.scrollLeft !== e.target.scrollLeft) {
                header.scrollLeft = e.target.scrollLeft;
              }
            }}
            data-timeline-body
          >
            {/* Grid Lines */}
            <div 
              className="absolute top-0 bottom-0 left-0 pointer-events-none flex"
              style={{ width: timelineWidth, minWidth: '100%', zIndex: 1 }}
            >
              {timeline.map((_, idx) => (
                <div 
                  key={idx} 
                  className="border-r border-gray-100"
                  style={{ width: divisionWidths[idx] || 100, minWidth: 60 }}
                />
              ))}
            </div>
            
            {/* Release Bands */}
            {showReleaseBands && releases.length > 0 && (
              <div 
                className="absolute top-0 bottom-0 left-0 pointer-events-none"
                style={{ width: timelineWidth, minWidth: '100%', zIndex: 2 }}
              >
                {releases.map((release, idx) => {
                  const start = parseDate(release.start_date);
                  const end = parseDate(release.end_date);
                  if (!start || !end) return null;
                  
                  const startOffset = Math.max(0, daysBetween(timelineStart, start));
                  const endOffset = daysBetween(timelineStart, end);
                  
                  const leftPct = Math.max(0, (startOffset / totalDays) * 100);
                  const widthPct = Math.min(100 - leftPct, ((endOffset - startOffset) / totalDays) * 100);
                  
                  if (widthPct <= 0) return null;
                  
                  const bandColors = [
                    'rgba(57, 73, 171, 0.08)',
                    'rgba(25, 118, 210, 0.08)',
                    'rgba(56, 142, 60, 0.08)',
                    'rgba(245, 124, 0, 0.08)'
                  ];
                  
                  return (
                    <div
                      key={release.id || idx}
                      className="absolute top-0 bottom-0"
                      style={{ 
                        left: `${leftPct}%`, 
                        width: `${widthPct}%`,
                        backgroundColor: bandColors[idx % bandColors.length]
                      }}
                    >
                      <span className="absolute top-1 left-1 text-[10px] font-medium text-[var(--clr-text-muted)] bg-white/70 px-1 py-0.5 rounded">
                        {release.name}
                      </span>
                    </div>
                  );
                })}
              </div>
            )}
            
            {/* Bars */}
            <div style={{ width: timelineWidth, minWidth: '100%' }}>
              {flattenedItems.map(({ item }) => (
                <TimelineBar
                  key={item.id}
                  item={item}
                  timelineStart={timelineStart}
                  totalDays={totalDays}
                  timelineWidth={timelineWidth}
                  onItemClick={onItemClick}
                  onOpenInspector={onOpenInspector}
                  currency={currency}
                  tenantId={tenantId}
                />
              ))}
            </div>
          </div>
        </div>
      </div>
      
      {/* Context Menu */}
      {contextMenu && canEdit() && (
        <div 
          className="fixed z-50 bg-[var(--clr-surface-card)] rounded-lg shadow-xl border py-1 min-w-40"
          style={{ top: contextMenu.y, left: contextMenu.x }}
        >
          <button
            className="w-full px-4 py-2 text-left text-sm hover:bg-[var(--clr-surface-hover)] flex items-center gap-2"
            onClick={() => { onEditItem && onEditItem(contextMenu.item); closeContextMenu(); }}
          >
            <Edit2 className="w-4 h-4" /> Edit
          </button>
          <button
            className="w-full px-4 py-2 text-left text-sm hover:bg-[var(--clr-surface-hover)] flex items-center gap-2"
            onClick={() => { onCreateItem && onCreateItem(contextMenu.item); closeContextMenu(); }}
          >
            <Plus className="w-4 h-4" /> Add Child
          </button>
          <hr className="my-1" />
          <button
            className="w-full px-4 py-2 text-left text-sm hover:bg-[var(--clr-sev-blocked)] bg-opacity-10 text-[var(--clr-sev-blocked)] flex items-center gap-2"
            onClick={() => { onDeleteItem && onDeleteItem(contextMenu.item); closeContextMenu(); }}
          >
            <Trash2 className="w-4 h-4" /> Archive
          </button>
        </div>
      )}
      
      {/* Locked indicator for lower tiers */}
      {!canEdit() && (
        <div className="absolute bottom-4 right-4 bg-[var(--clr-brand-primary)] bg-opacity-10 text-[var(--clr-brand-primary)] px-3 py-2 rounded-lg text-sm flex items-center gap-2 shadow-[var(--shadow-card)]">
          <Lock className="w-4 h-4" />
          <span>Upgrade to edit items</span>
        </div>
      )}
    </div>
  );
};

export default HierarchicalRoadmap;
